import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileWarning, Users, HandHeart, Inbox, Newspaper, CalendarDays, MessageCircle, CheckCircle2, AlertTriangle,
  Clock, Bell, Mail, ImageOff, ShieldCheck, Plus, BarChart3, FileBarChart, Rocket, Sparkles, CircleDot,
  Send, Copy, Pencil, CalendarClock, Network, FileBarChart2, Eye, XCircle, Palette, Trophy, QrCode, MapPin, Printer, Loader2, Package, ListChecks, DoorOpen, Activity,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { api, fileUrl } from '../../lib/api';
import { Loading, formatDate } from '../../components/Shared';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const Stat = ({ icon: Icon, label, value, to, accent, testId }) => (
  <Link to={to || '#'} data-testid={testId || `admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <Card className="card-lift h-full">
      <CardContent className="p-4 flex items-center gap-3">
        <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${accent || 'bg-[#EAF2FF] text-[#1a2744]'}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="text-xl font-black text-[#1a2744] leading-none truncate">{value ?? '—'}</p>
          <p className="text-[11px] font-semibold text-slate-500 mt-1 leading-tight">{label}</p>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const ChartCard = ({ title, children, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <p className="font-serif font-bold text-[#1a2744] mb-3 text-sm">{title}</p>
      <div className="h-52 overflow-x-auto"><div className="h-full min-w-[280px]">{children}</div></div>
    </CardContent>
  </Card>
);

const QUICK_ACTIONS = [
  { label: 'Add Issue', to: '/issue-tracker/report', icon: FileWarning },
  { label: 'Add Meeting', to: '/admin/meetings', icon: CalendarDays },
  { label: 'Add Article', to: '/admin/newsroom', icon: Newspaper },
  { label: 'Add Alert', to: '/admin/alerts', icon: Bell },
  { label: 'Add Chart', to: '/admin/city-data', icon: BarChart3 },
  { label: 'Add Report', to: '/admin/reports', icon: FileBarChart },
  { label: 'Review Samples', to: '/admin/launch', icon: Sparkles },
  { label: 'Media Review', to: '/admin/media-review', icon: ImageOff },
  { label: 'Launch Audit', to: '/admin/launch', icon: Rocket },
];

const JOIN_OFFICE_LABELS = { ward: 'Ward Councillor', citywide: 'Citywide / team', mayor: 'Mayor' };
const JOIN_STATUS_LABELS = { new: 'New', needs_review: 'Needs Review', approved: 'Approved', declined: 'Declined', contacted: 'Contacted' };

const NetworkJoinAlert = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/admin/network/join-summary').then((r) => setData(r.data)).catch(() => setData({ new_count: 0, latest: null })); }, []);

  return (
    <Card className="mb-6" data-testid="dashboard-join-alert">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
            <Network className="h-4 w-4 text-[#C9A84C]" /> New Tracker Network Requests
            {data?.new_count > 0 && <Badge className="bg-[#C9A84C] text-[#1a2744] font-black text-[11px]" data-testid="join-alert-count">{data.new_count}</Badge>}
          </p>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="join-alert-review-btn">
            <Link to="/admin/network?tab=join">Review Requests</Link>
          </Button>
        </div>
        {data === null ? (
          <p className="text-sm text-slate-400 py-1">Loading…</p>
        ) : data.new_count === 0 ? (
          <p className="text-sm text-slate-500 py-1" data-testid="join-alert-empty">No new tracker network requests.</p>
        ) : (
          <div className="rounded-xl border border-slate-200 p-3" data-testid="join-alert-latest">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="font-bold text-[#1a2744] text-sm">{data.latest?.name}</p>
              <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">
                {JOIN_OFFICE_LABELS[data.latest?.office] || data.latest?.office}{data.latest?.ward ? ` · Ward ${data.latest.ward}` : ''}
              </Badge>
              <Badge variant="outline" className="text-[10px] font-bold bg-blue-50 text-blue-800 border-blue-200">{JOIN_STATUS_LABELS[data.latest?.status] || data.latest?.status}</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Submitted {(data.latest?.created_at || '').slice(0, 10)}{data.new_count > 1 ? ` · plus ${data.new_count - 1} more awaiting review` : ''}</p>
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">Requests are private until reviewed. No emails are sent automatically.</p>
      </CardContent>
    </Card>
  );
};

const MonthlyReportReminder = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/admin/network/report-reminder').then((r) => setState(r.data)).catch(() => setState(null));
  useEffect(() => { load(); }, []);

  const generate = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.post('/admin/network/report', { report_type: 'monthly_ward', tracker_slug: 'ward-8', use_ai: true });
      toast.success(kind === 'draft'
        ? 'Draft article created in Admin → Newsroom — review before publishing'
        : `Ward 8 report generated (${r.data.stats.total} tracked) — draft only, nothing published`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const dismiss = async () => {
    setBusy('dismiss');
    try { await api.post('/admin/network/report-reminder/dismiss'); toast.success('Reminder dismissed for this month'); load(); }
    catch { toast.error('Failed'); }
    setBusy('');
  };
  const toggle = async (v) => {
    try { await api.put('/admin/network/report-reminder', { enabled: v }); toast.success(v ? 'Monthly reminder ON' : 'Monthly reminder OFF'); load(); }
    catch { toast.error('Failed'); }
  };

  if (!state) return null;
  return (
    <Card className="mb-6" data-testid="dashboard-report-reminder">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><FileBarChart2 className="h-4 w-4 text-[#C9A84C]" /> Monthly Issue Tracker Report Reminder</p>
          <label className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
            {state.enabled ? 'ON' : 'OFF'}
            <Switch checked={state.enabled} onCheckedChange={toggle} data-testid="reminder-toggle" />
          </label>
        </div>
        {state.show ? (
          <>
            <p className="text-sm text-slate-600 mt-2" data-testid="reminder-message">It may be time to generate and review the Ward 8 Public Issue Tracker monthly report.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" onClick={() => generate('report')} disabled={!!busy} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="reminder-generate-btn">
                <FileBarChart2 className="h-3.5 w-3.5 mr-1" /> Generate Ward 8 Report
              </Button>
              <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid="reminder-view-btn">
                <Link to="/admin/network?tab=reports"><Eye className="h-3.5 w-3.5 mr-1" /> View Reports</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid="reminder-weekly-btn">
                <Link to="/admin/issues#weekly-digest"><CalendarClock className="h-3.5 w-3.5 mr-1" /> Weekly Digest</Link>
              </Button>
              <Button size="sm" variant="outline" onClick={() => generate('draft')} disabled={!!busy} className="h-9 text-xs font-bold" data-testid="reminder-draft-btn">
                <Newspaper className="h-3.5 w-3.5 mr-1" /> Create Newsroom Draft
              </Button>
              <Button size="sm" variant="outline" onClick={dismiss} disabled={!!busy} className="h-9 text-xs font-bold text-slate-500" data-testid="reminder-dismiss-btn">
                <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss This Month
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2.5">Reports are created as drafts only — nothing is generated on a timer or published automatically.</p>
          </>
        ) : (
          <p className="text-sm text-slate-500 mt-2" data-testid="reminder-idle">
            {state.enabled ? 'Dismissed for this month — the reminder returns next month.' : 'Reminder is turned off.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const WeeklyDigestNotice = () => {
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/admin/tracker-digest/notice').then((r) => setNotice(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const attach = async () => {
    setBusy('attach');
    try {
      await api.post(`/admin/tracker-digest/weekly/${notice.article_id}/newsletter`);
      toast.success('Draft newsletter created — nothing is sent automatically');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy('');
  };
  const dismiss = async () => {
    setBusy('dismiss');
    try { await api.post('/admin/tracker-digest/notice/dismiss'); load(); }
    catch { toast.error('Failed'); }
    setBusy('');
  };

  if (!notice?.show) return null;
  return (
    <Card className="mb-6 border-[#C9A84C]/50" data-testid="dashboard-digest-notice">
      <CardContent className="p-4">
        <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
          <Newspaper className="h-4 w-4 text-[#C9A84C]" /> Weekly Digest Draft Ready for Review
        </p>
        <p className="text-sm text-slate-600 mt-1.5" data-testid="digest-notice-title">"{notice.title}" was auto-drafted. Review it before anything goes out — nothing publishes or emails automatically.</p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button asChild size="sm" className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="digest-notice-review-btn">
            <Link to="/admin/issues#weekly-digest"><Eye className="h-3.5 w-3.5 mr-1" /> Review Draft</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold" data-testid="digest-notice-edit-btn">
            <Link to="/admin/newsroom"><Newspaper className="h-3.5 w-3.5 mr-1" /> Edit in Newsroom</Link>
          </Button>
          <Button size="sm" variant="outline" onClick={attach} disabled={!!busy} className="h-9 text-xs font-bold" data-testid="digest-notice-attach-btn">
            <Mail className="h-3.5 w-3.5 mr-1" /> Attach to Newsletter
          </Button>
          <Button size="sm" variant="outline" onClick={dismiss} disabled={!!busy} className="h-9 text-xs font-bold text-slate-500" data-testid="digest-notice-dismiss-btn">
            <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- Phase 50 Part 4: Design Reminder Nudge (admin-only; no auto-approval, publishing, or emails) ----
const DesignReminderCard = () => {
  const [data, setData] = useState(null);
  const load = () => api.get('/admin/designs/reminders').then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const dismiss = async (item) => {
    try {
      await api.post(`/admin/designs/versions/${item.id}/dismiss-reminder`);
      toast.success('Reminder dismissed — the version stays private and unapproved.');
      load();
    } catch { toast.error('Failed to dismiss'); }
  };

  if (!data || data.items.length === 0) return null;
  return (
    <Card className="mb-6 border-[#C9A84C]/50" data-testid="dashboard-design-reminder">
      <CardContent className="p-4">
        <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
          <Palette className="h-4 w-4 text-[#C9A84C]" /> Design Waiting for Approval
          <Badge className="bg-[#C9A84C] text-[#1a2744] font-black text-[11px]" data-testid="design-reminder-count">{data.items.length}</Badge>
        </p>
        <div className="space-y-2 mt-2.5">
          {data.items.slice(0, 3).map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`design-reminder-item-${item.id}`}>
              <div className="min-w-0">
                <p className="font-bold text-[#1a2744] text-sm truncate">{item.design_label}</p>
                <p className="text-xs text-slate-500 mt-0.5 break-all">
                  {item.file_name} · uploaded {(item.uploaded_at || '').slice(0, 10)} · waiting {item.days_waiting} days · {item.status}
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button asChild size="sm" className="h-8 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid={`design-reminder-review-${item.id}`}>
                  <Link to="/admin/designs"><Eye className="h-3 w-3 mr-1" /> Review Design</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={() => dismiss(item)} className="h-8 text-xs font-bold text-slate-500" data-testid={`design-reminder-dismiss-${item.id}`}>
                  <XCircle className="h-3 w-3 mr-1" /> Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5">Gentle reminder only — nothing is approved, published, or emailed automatically.</p>
      </CardContent>
    </Card>
  );
};

const SetupRemindersCard = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get('/admin/setup-reminders').then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const dismiss = async (id) => {
    try {
      await api.post(`/admin/setup-reminders/${id}/dismiss`);
      toast.success('Setup reminder dismissed');
      load();
    } catch { toast.error('Dismiss failed'); }
  };

  if (!items.length) return null;
  return (
    <div className="space-y-3 mb-6" data-testid="dashboard-setup-reminders">
      {items.map((it) => (
        <Card key={it.id} className="border-dashed border-[#C9A84C]/60" data-testid={`setup-reminder-${it.id.replace(/_/g, '-')}`}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><ListChecks className="h-4 w-4 text-[#C9A84C]" /> {it.title}</p>
              <p className="text-xs text-slate-500 mt-1">{it.body}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button asChild size="sm" className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid={`setup-reminder-open-${it.id.replace(/_/g, '-')}`}>
                <Link to={it.link}>{it.button}</Link>
              </Button>
              <Button size="sm" variant="ghost" onClick={() => dismiss(it.id)} className="h-9 text-xs font-bold text-slate-500" data-testid={`setup-reminder-dismiss-${it.id.replace(/_/g, '-')}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const WeeklyBriefReminderCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState('');
  const load = () => api.get('/admin/weekly-brief-reminder').then((r) => { setData(r.data); setPdf(r.data.last_pdf || ''); }).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const generate = async (openAfter) => {
    setBusy(true);
    try {
      const r = await api.post('/admin/field-ops-brief', {});
      setPdf(r.data.pdf);
      toast.success('Weekly Ops Brief generated');
      if (openAfter) window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      load();
    } catch { toast.error('Brief generation failed'); }
    setBusy(false);
  };

  const printPdf = () => {
    if (pdf) window.open(fileUrl(pdf), '_blank', 'noopener');
    else generate(true);
  };

  const dismiss = async () => {
    try {
      await api.post('/admin/weekly-brief-reminder/dismiss');
      toast.success('Reminder dismissed — it returns next Monday.');
      load();
    } catch { toast.error('Dismiss failed'); }
  };

  if (!data || !data.show) return null;
  return (
    <Card className="mb-6 border-[#C9A84C]/50" data-testid="dashboard-weekly-brief-reminder">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
            <Printer className="h-4 w-4 text-[#C9A84C]" /> Print Weekly Ops Brief
            <span className="text-[10px] font-sans font-bold text-slate-400" data-testid="weekly-brief-week-range">week of {data.week_start} to {data.week_end}</span>
            {data.generated_this_week && <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-bold" data-testid="weekly-brief-generated-badge">Generated this week</Badge>}
          </p>
        </div>
        <p className="text-xs text-slate-500 mt-1.5">Monday habit: print this week's one-page field ops brief for the campaign binder.</p>
        <div className="flex flex-wrap gap-2 mt-2.5">
          <Button size="sm" onClick={() => generate(false)} disabled={busy} className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="weekly-brief-generate-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileBarChart2 className="h-3.5 w-3.5 mr-1" />} Generate Brief
          </Button>
          <Button size="sm" variant="outline" onClick={printPdf} disabled={busy} className="h-9 text-xs font-bold btn-press" data-testid="weekly-brief-print-btn">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print PDF
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss} className="h-9 text-xs font-bold text-slate-500" data-testid="weekly-brief-dismiss-btn">
            <XCircle className="h-3.5 w-3.5 mr-1" /> Dismiss This Week
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">{data.note}</p>
      </CardContent>
    </Card>
  );
};

const LaunchCountdownBanner = () => {
  const [lc, setLc] = useState(null);
  useEffect(() => {
    api.get('/admin/launch-countdown').then((r) => setLc(r.data)).catch(() => setLc(null));
  }, []);
  if (!lc) return null;

  if (!lc.configured) {
    return (
      <div className="mb-5 rounded-xl border border-dashed border-[#C9A84C]/60 bg-[#C9A84C]/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="launch-countdown-setup">
        <div className="flex items-start gap-3 min-w-0">
          <Rocket className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
          <div>
            <p className="font-serif font-bold text-[#1a2744] text-sm">{lc.setup_prompt}</p>
            <p className="text-xs text-slate-500 mt-0.5">Pick Steve's campaign launch date in Election Centre Key Dates to start the private launch countdown.</p>
          </div>
        </div>
        <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid="launch-countdown-open-key-dates">
          <Link to={lc.key_dates_link}><CalendarClock className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Key Dates</Link>
        </Button>
      </div>
    );
  }

  const past = lc.days_until < 0;
  return (
    <div className="mb-5 rounded-xl border border-[#1a2744]/20 bg-[#1a2744] p-4 text-white" data-testid="launch-countdown-banner">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <div className="shrink-0 text-center rounded-xl bg-white/10 px-4 py-2">
            <p className="font-serif font-black text-3xl text-[#C9A84C] leading-none" data-testid="launch-countdown-days">{past ? 0 : lc.days_until}</p>
            <p className="text-[10px] font-bold uppercase tracking-wide text-white/70 mt-1">{past ? 'launched' : lc.days_until === 1 ? 'day to go' : 'days to go'}</p>
          </div>
          <div className="min-w-0">
            <p className="font-serif font-bold text-base">{past ? 'Campaign launched' : 'Campaign launch countdown'} · <span className="text-[#C9A84C]" data-testid="launch-countdown-date">{lc.launch_date}</span></p>
            <p className="text-xs text-white/70 mt-0.5 truncate" data-testid="launch-countdown-next-task">Next: {lc.next_task}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white btn-press" data-testid="launch-countdown-domain-link">
            <Link to={lc.links.domain_activation}>Domain Activation</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white btn-press" data-testid="launch-countdown-runbook-link">
            <Link to={lc.links.launch_runbook}>Launch Day Runbook</Link>
          </Button>
          <Button asChild size="sm" variant="outline" className="h-9 text-xs font-bold bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white btn-press" data-testid="launch-countdown-package-link">
            <Link to={lc.links.launch_package}>Final Launch Package</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const ElectionCountdownPanel = () => {
  const [cd, setCd] = useState(null);
  const [dates, setDates] = useState({ campaign_launch: '', advance_voting_start: '' });
  const [editing, setEditing] = useState(false);
  const load = () => api.get('/admin/election-countdown').then((r) => setCd(r.data)).catch(() => setCd(null));
  useEffect(() => { load(); }, []);

  const saveDates = async () => {
    try {
      await api.put('/admin/election-countdown/dates', dates);
      toast.success('Key election dates saved');
      setEditing(false);
      load();
    } catch { toast.error('Saving dates failed'); }
  };

  if (!cd) return null;
  return (
    <div className="mt-3 rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-3" data-testid="election-countdown-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Election Countdown</p>
        <div className="flex flex-wrap gap-1.5">
          <Button asChild size="sm" variant="outline" className="h-7 text-[11px] font-bold" data-testid="countdown-key-dates-link">
            <Link to="/admin/election?tab=key-dates"><CalendarClock className="h-3 w-3 mr-1" /> Key Dates</Link>
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} className="h-7 text-[11px] font-bold" data-testid="countdown-edit-dates-btn">
            <Pencil className="h-3 w-3 mr-1" /> {editing ? 'Close' : 'Set Dates'}
          </Button>
        </div>
      </div>
      {cd.needs_setup && !editing && (
        <button onClick={() => setEditing(true)} className="mt-2 w-full text-left rounded-lg bg-white border border-dashed border-[#C9A84C]/60 p-2.5 hover:bg-[#C9A84C]/10" data-testid="countdown-setup-prompt">
          <p className="text-xs font-bold text-[#7A5C12]">{cd.setup_prompt}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">Add your campaign launch and advance voting dates — the final voting day is already pulled from the Election Centre.</p>
        </button>
      )}
      {editing && (
        <div className="mt-2 rounded-lg bg-white border border-slate-200 p-3" data-testid="countdown-dates-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Campaign launch date</label>
              <Input type="date" value={dates.campaign_launch} onChange={(e) => setDates((d) => ({ ...d, campaign_launch: e.target.value }))} className="h-10" data-testid="countdown-input-launch" />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Advance voting starts</label>
              <Input type="date" value={dates.advance_voting_start} onChange={(e) => setDates((d) => ({ ...d, advance_voting_start: e.target.value }))} className="h-10" data-testid="countdown-input-advance" />
            </div>
          </div>
          <Button size="sm" onClick={saveDates} className="h-9 mt-2.5 bg-[#1a2744] text-white font-bold" data-testid="countdown-save-dates-btn">Save Dates</Button>
        </div>
      )}
      {cd.entries.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2.5">
          {cd.entries.filter((e) => !e.past).slice(0, 4).map((e) => (
            <div key={`${e.label}-${e.date}`} className={`rounded-lg border p-2.5 bg-white ${cd.next_date && cd.next_date.date === e.date && cd.next_date.label === e.label ? 'border-[#C9A84C]' : 'border-slate-200'}`} data-testid={`countdown-entry-${e.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <p className="font-serif font-black text-xl text-[#1a2744]">{e.days_until}<span className="text-xs font-sans font-bold text-slate-400 ml-1">days</span></p>
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 leading-tight">{e.label}</p>
              <p className="text-[10px] text-slate-400">{e.date}</p>
            </div>
          ))}
        </div>
      )}
      {cd.pacing_note && <p className="text-[11px] font-semibold text-[#7A5C12] mt-2" data-testid="countdown-pacing-note">{cd.pacing_note}</p>}
      <p className="text-[10px] text-slate-400 mt-1">{cd.note}</p>
    </div>
  );
};

const MaterialsLowCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/admin/materials').then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data || data.low.length === 0) return null;
  return (
    <Card className="mb-6 border-amber-200" data-testid="dashboard-materials-low">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
            <Package className="h-4 w-4 text-[#C9A84C]" /> Materials Running Low
            <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-black text-[11px]" data-testid="materials-low-count">{data.low.length}</Badge>
          </p>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="materials-low-open-btn">
            <Link to="/admin/materials"><Package className="h-3.5 w-3.5 mr-1" /> Material Inventory</Link>
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2.5">
          {data.low.slice(0, 6).map((m) => (
            <Badge key={m.id} variant="outline" className="text-xs font-bold bg-amber-50 border-amber-300 text-amber-900" data-testid={`material-low-${m.id}`}>
              {m.name}: {m.remaining} left (reorder at {m.reorder_threshold})
            </Badge>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Private reminder only — nothing is ordered or emailed automatically.</p>
      </CardContent>
    </Card>
  );
};

const CanvassSummarySection = () => {
  const [cs, setCs] = useState(null);
  useEffect(() => { api.get('/admin/canvass-summary').then((r) => setCs(r.data)).catch(() => setCs(null)); }, []);
  if (!cs) return null;
  const stats = [
    { label: 'Streets planned', value: cs.streets_planned, testId: 'canvass-streets-planned' },
    { label: 'Streets completed', value: cs.streets_completed, testId: 'canvass-streets-completed' },
    { label: 'Doors attempted', value: cs.doors_attempted, testId: 'canvass-doors' },
    { label: 'Conversations', value: cs.conversations, testId: 'canvass-conversations' },
    { label: 'Materials left', value: cs.materials_left, testId: 'canvass-materials-left' },
    { label: 'Follow-ups due', value: cs.follow_ups_due, testId: 'canvass-followups-due' },
  ];
  return (
    <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="dashboard-canvass-summary">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" /> Canvass Summary</p>
        <Button asChild size="sm" variant="outline" className="h-7 text-[11px] font-bold" data-testid="canvass-summary-open-btn">
          <Link to="/admin/canvass"><DoorOpen className="h-3 w-3 mr-1" /> Canvass Log</Link>
        </Button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {stats.map((s) => (
          <div key={s.testId} className="rounded-lg border border-slate-200 p-2" data-testid={s.testId}>
            <p className="font-serif font-black text-lg text-[#1a2744]">{s.value}</p>
            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500 leading-tight">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs text-slate-600">
        <p data-testid="canvass-communities-covered"><span className="font-bold text-[#1a2744]">Communities canvassed:</span> {cs.communities_canvassed.length ? cs.communities_canvassed.join(', ') : 'None yet'}</p>
        <p data-testid="canvass-communities-needing"><span className="font-bold text-[#1a2744]">Needing canvass coverage:</span> {cs.communities_needing_canvass.length ? cs.communities_needing_canvass.join(', ') : 'None'}</p>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">{cs.note}</p>
    </div>
  );
};

const FieldOpsSummaryCard = () => {
  const [fo, setFo] = useState(null);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [printing, setPrinting] = useState(false);
  useEffect(() => { api.get('/admin/field-ops-summary').then((r) => setFo(r.data)).catch(() => setFo(null)); }, []);
  useEffect(() => {
    if (fo && window.location.hash === '#field-ops-summary') {
      const el = document.getElementById('field-ops-summary');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [fo]);

  const printBrief = async () => {
    setPrinting(true);
    try {
      const r = await api.post('/admin/field-ops-brief', { include_private_notes: includeNotes });
      window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      toast.success('Weekly Ops Brief ready to print');
    } catch { toast.error('Brief generation failed'); }
    setPrinting(false);
  };

  if (!fo) return null;
  const stats = [
    { label: 'Drops this week', value: fo.drops_completed_week, testId: 'field-ops-drops-week' },
    { label: 'Planned drops', value: fo.planned_drops, testId: 'field-ops-planned' },
    { label: 'Follow-ups due', value: fo.followups_due, testId: 'field-ops-followups' },
    { label: 'QR scans this week', value: fo.qr_scans_week, sub: `${fo.qr_change >= 0 ? '+' : ''}${fo.qr_change} vs last week`, testId: 'field-ops-qr-week' },
  ];
  return (
    <Card className="mb-6 scroll-mt-20" id="field-ops-summary" data-testid="dashboard-field-ops-summary">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#C9A84C]" /> Field Ops Summary <span className="text-[10px] font-sans font-bold text-slate-400">week of {fo.week_start}</span></p>
          <div className="flex flex-wrap gap-1.5">
            <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="field-ops-materials-btn">
              <Link to="/admin/materials"><Package className="h-3.5 w-3.5 mr-1" /> Materials</Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="field-ops-open-drops-btn">
              <Link to="/admin/poster-drops"><MapPin className="h-3.5 w-3.5 mr-1" /> Poster Drop List</Link>
            </Button>
            <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="field-ops-coverage-snapshot-btn">
              <Link to="/admin/poster-drops?tab=coverage"><BarChart3 className="h-3.5 w-3.5 mr-1" /> Coverage Snapshot</Link>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {stats.map((s) => (
            <div key={s.testId} className="rounded-xl border border-slate-200 p-3" data-testid={s.testId}>
              <p className="font-serif font-black text-2xl text-[#1a2744]">{s.value}</p>
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{s.label}</p>
              {s.sub && <p className="text-[10px] text-slate-400 mt-0.5">{s.sub}</p>}
            </div>
          ))}
        </div>
        {fo.goal_target_total > 0 && (
          <div className="mt-3 rounded-xl border border-slate-200 p-3" data-testid="field-ops-goal-progress">
            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 mb-1">
              <span>Coverage goals: {fo.goal_completed_total}/{fo.goal_target_total} drops placed</span>
              <span>{fo.goal_progress_pct}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#C9A84C]" style={{ width: `${fo.goal_progress_pct || 0}%` }} /></div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-3 text-xs text-slate-600">
          <p data-testid="field-ops-covered"><span className="font-bold text-[#1a2744]">Communities covered:</span> {fo.communities_covered.length ? fo.communities_covered.join(', ') : 'None yet'}</p>
          <p data-testid="field-ops-needing-more"><span className="font-bold text-[#1a2744]">Needing more coverage:</span> {fo.communities_needing_more.length ? fo.communities_needing_more.join(', ') : 'None'}</p>
          <p data-testid="field-ops-top-qr"><span className="font-bold text-[#1a2744]">Top QR source:</span> {fo.top_qr_source ? `${fo.top_qr_source.label} (${fo.top_qr_source.this_week} this week)` : 'No scans yet'}</p>
          <p data-testid="field-ops-upcoming"><span className="font-bold text-[#1a2744]">Upcoming drop days:</span> {fo.upcoming_drop_days.length ? fo.upcoming_drop_days.map((d) => `${d.date}${d.area ? ` (${d.area})` : ''}`).join(', ') : 'None planned'}</p>
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5">{fo.note}</p>

        {/* Phase 59: canvass summary */}
        <CanvassSummarySection />

        {/* Phase 56: election countdown */}
        <ElectionCountdownPanel />

        {/* Phase 56: weekly ops print */}
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button onClick={printBrief} disabled={printing} className="h-10 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="print-ops-brief-btn">
            {printing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} Print Weekly Ops Brief
          </Button>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="ops-brief-notes-label">
            <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#1a2744]" data-testid="ops-brief-include-notes" />
            Include private notes
          </label>
          <p className="text-[10px] text-slate-400">Off by default — the brief never includes volunteer contact details.</p>
        </div>
      </CardContent>
    </Card>
  );
};

const DropDayRemindersCard = () => {
  const [data, setData] = useState(null);
  const [printingId, setPrintingId] = useState('');
  const load = () => api.get('/admin/drop-day-reminders').then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const dismiss = async (item) => {
    try {
      await api.post(`/admin/drop-days/${item.id}/dismiss-reminder`);
      toast.success('Reminder dismissed — the drop day stays in the planner.');
      load();
    } catch { toast.error('Dismiss failed'); }
  };

  const printSheet = async (item) => {
    if (!item.locations.length) { toast.error('No locations assigned to this drop day yet'); return; }
    setPrintingId(item.id);
    try {
      const r = await api.post('/admin/poster-drops/route-sheet', { location_ids: item.locations.map((l) => l.id) });
      window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
      toast.success('Route sheet ready');
    } catch { toast.error('Route sheet generation failed'); }
    setPrintingId('');
  };

  if (!data || data.items.length === 0) return null;
  return (
    <Card className="mb-6 border-[#1a2744]/20" data-testid="dashboard-drop-day-reminders">
      <CardContent className="p-4">
        <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[#C9A84C]" /> Drop Day Scheduled
          <Badge className="bg-[#C9A84C]/15 text-[#7A5C12] border border-[#C9A84C]/50 font-black text-[11px]" data-testid="drop-day-reminders-count">{data.count}</Badge>
        </p>
        <div className="space-y-2 mt-2.5">
          {data.items.slice(0, 3).map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 p-3" data-testid={`drop-day-reminder-${item.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="font-bold text-[#1a2744] text-sm">{item.date}</p>
                    {item.area && <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{item.area}</Badge>}
                    {item.overdue && <Badge variant="outline" className="text-[10px] font-bold bg-red-50 text-red-800 border-red-200">Overdue</Badge>}
                    {item.followups_due > 0 && <Badge variant="outline" className="text-[10px] font-bold bg-amber-50 text-amber-900 border-amber-300">{item.followups_due} follow-up{item.followups_due === 1 ? '' : 's'} due</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {item.volunteers.length ? `Volunteers: ${item.volunteers.join(', ')}` : 'No volunteer assigned'} · {item.location_count} location{item.location_count === 1 ? '' : 's'}
                    {Object.keys(item.materials_needed).length ? ` · Materials: ${Object.entries(item.materials_needed).map(([m, c]) => `${m} × ${c}`).join(', ')}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`drop-day-view-${item.id}`}>
                    <Link to="/admin/poster-drops?tab=planner"><Eye className="h-3 w-3 mr-1" /> View Drop Day</Link>
                  </Button>
                  <Button size="sm" onClick={() => printSheet(item)} disabled={printingId === item.id} className="h-8 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid={`drop-day-print-${item.id}`}>
                    {printingId === item.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Printer className="h-3 w-3 mr-1" />} Print Route Sheet
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => dismiss(item)} className="h-8 text-xs font-bold text-slate-500" data-testid={`drop-day-dismiss-${item.id}`}>
                    <XCircle className="h-3 w-3 mr-1" /> Dismiss
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5">{data.note}</p>
      </CardContent>
    </Card>
  );
};

const ReprintAlertCard = () => {
  const [alert, setAlert] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/admin/qr-reprint-alert').then((r) => setAlert(r.data)).catch(() => setAlert(null));
  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    try {
      await api.post('/admin/qr-reprint-alert/dismiss');
      toast.success('Reprint suggestion dismissed. It returns if scans keep climbing.');
      load();
    } catch { toast.error('Dismiss failed'); }
  };

  const generateReviewSet = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/print-review-set');
      toast.success('Print Review Set generated');
      if (r.data?.pdf) window.open(fileUrl(r.data.pdf), '_blank', 'noopener');
    } catch { toast.error('Generation failed — open Launch Content to review print tools'); }
    setBusy(false);
  };

  if (!alert?.show) return null;
  return (
    <Card className="mb-6 border-[#C9A84C]/50" data-testid="dashboard-reprint-alert">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
              <Printer className="h-4 w-4 text-[#C9A84C]" /> Print Piece Performing Well
            </p>
            <p className="text-sm text-slate-700 mt-1.5">
              <span className="font-black text-[#1a2744]" data-testid="reprint-alert-piece">{alert.label}</span>
              {' '}has <span className="font-bold" data-testid="reprint-alert-total">{alert.total} scans</span> — {alert.comparison}. Worth considering more of this piece.
            </p>
            <p className="text-[10px] text-slate-400 mt-1">{alert.note}</p>
          </div>
          <div className="flex flex-wrap gap-1.5 shrink-0">
            <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="reprint-alert-leaderboard-btn">
              <Link to="/admin/launch-content#launch-section-qr-analytics"><Trophy className="h-3 w-3 mr-1" /> View QR Leaderboard</Link>
            </Button>
            <Button size="sm" onClick={generateReviewSet} disabled={busy} className="h-8 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold" data-testid="reprint-alert-review-set-btn">
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Printer className="h-3 w-3 mr-1" />} Generate Print Review Set
            </Button>
            <Button size="sm" variant="outline" onClick={dismiss} className="h-8 text-xs font-bold text-slate-500" data-testid="reprint-alert-dismiss-btn">
              <XCircle className="h-3 w-3 mr-1" /> Dismiss
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PosterFollowUpsCard = () => {
  const [data, setData] = useState(null);
  const load = () => api.get('/admin/poster-drops/reminders').then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const markDone = async (item) => {
    try {
      await api.post(`/admin/poster-drops/${item.id}/mark-followed-up`);
      toast.success(`"${item.location_name}" marked followed up`);
      load();
    } catch { toast.error('Update failed'); }
  };
  const dismiss = async (item) => {
    try {
      await api.post(`/admin/poster-drops/${item.id}/dismiss-reminder`);
      toast.success('Reminder dismissed — the follow-up flag stays on the entry.');
      load();
    } catch { toast.error('Dismiss failed'); }
  };

  if (!data || data.items.length === 0) return null;
  return (
    <Card className="mb-6 border-amber-200" data-testid="dashboard-poster-followups">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#C9A84C]" /> Poster Follow-Ups Due
            <Badge className="bg-amber-100 text-amber-900 border border-amber-300 font-black text-[11px]" data-testid="poster-followups-count">{data.count}</Badge>
          </p>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="poster-followups-open-list-btn">
            <Link to="/admin/poster-drops"><MapPin className="h-3.5 w-3.5 mr-1" /> Poster Drop List</Link>
          </Button>
        </div>
        <div className="space-y-2 mt-2.5">
          {data.items.slice(0, 4).map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`poster-followup-item-${item.id}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <p className="font-bold text-[#1a2744] text-sm truncate">{item.location_name}</p>
                  <Badge variant="outline" className={`text-[10px] font-bold ${item.overdue ? 'bg-red-50 text-red-800 border-red-200' : 'bg-amber-50 text-amber-900 border-amber-300'}`}>
                    {item.overdue ? 'Overdue' : 'Due today'} · {item.follow_up_date}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{item.status_label}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {item.area || 'No area recorded'}{item.materials?.length ? ` · ${item.materials.join(', ')}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 shrink-0">
                <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`poster-followup-review-${item.id}`}>
                  <Link to="/admin/poster-drops"><Eye className="h-3 w-3 mr-1" /> Review Location</Link>
                </Button>
                <Button size="sm" onClick={() => markDone(item)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`poster-followup-done-${item.id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Followed Up
                </Button>
                <Button size="sm" variant="outline" onClick={() => dismiss(item)} className="h-8 text-xs font-bold text-slate-500" data-testid={`poster-followup-dismiss-${item.id}`}>
                  <XCircle className="h-3 w-3 mr-1" /> Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-2.5">{data.note}</p>
      </CardContent>
    </Card>
  );
};

const TopQrCodesCard = () => {
  const [board, setBoard] = useState(null);
  useEffect(() => { api.get('/admin/qr-leaderboard').then((r) => setBoard(r.data)).catch(() => setBoard(null)); }, []);

  if (!board) return null;
  const top = (board.rows || []).slice(0, 3);
  return (
    <Card className="mb-6" data-testid="dashboard-top-qr-codes">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-[#C9A84C]" /> Top Performing QR Codes</p>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="top-qr-open-leaderboard-btn">
            <Link to="/admin/launch-content#launch-section-qr-analytics"><QrCode className="h-3.5 w-3.5 mr-1" /> Full Leaderboard</Link>
          </Button>
        </div>
        {board.total === 0 ? (
          <p className="text-sm text-slate-500 py-1" data-testid="top-qr-empty">No scans recorded yet. Counters start the moment someone scans a printed QR code.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {top.map((r) => (
              <div key={r.source} className="rounded-xl border border-slate-200 p-3 flex items-center gap-2.5" data-testid={`top-qr-item-${r.source}`}>
                <span className={`h-8 w-8 rounded-lg flex items-center justify-center font-black text-sm shrink-0 ${r.rank === 1 ? 'bg-[#C9A84C]/20 text-[#7A5C12]' : 'bg-slate-100 text-slate-500'}`}>#{r.rank}</span>
                <div className="min-w-0">
                  <p className="font-bold text-[#1a2744] text-sm truncate">{r.label}</p>
                  <p className="text-xs text-slate-500">{r.total} scan{r.total === 1 ? '' : 's'} · {r.last7} this week</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">Aggregate counters only — no personal data, devices, or locations are tracked.</p>
      </CardContent>
    </Card>
  );
};

const SCHED_STATUS_STYLES = {
  approved: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
  scheduled: 'bg-blue-50 text-blue-800 border-blue-200',
};
const SCHED_PLATFORMS = { facebook: 'Facebook', newsroom: 'Newsroom', newsletter: 'Newsletter', website_alert: 'Website Alert' };

const UpcomingLaunchPosts = () => {
  const [items, setItems] = useState(null);
  const load = () => api.get('/admin/scheduler/upcoming').then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const copyItem = (item) => {
    const text = [item.body, item.link].filter(Boolean).join('\n\n') || item.title;
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard')).catch(() => toast.error('Copy failed'));
  };
  const markPosted = async (item) => {
    try {
      await api.put(`/admin/scheduler/${item.id}`, { status: 'posted' });
      toast.success('Marked as posted'); load();
    } catch { toast.error('Update failed'); }
  };

  return (
    <Card className="mb-6" data-testid="dashboard-upcoming-launch-posts">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="font-serif font-bold text-[#1a2744] text-sm flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#C9A84C]" /> Upcoming Launch Posts</p>
          <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid="upcoming-open-scheduler-btn">
            <Link to="/admin/scheduler"><Send className="h-3.5 w-3.5 mr-1" /> Post Scheduler</Link>
          </Button>
        </div>
        {items === null ? (
          <p className="text-sm text-slate-400 py-3">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 py-3" data-testid="upcoming-empty">No launch posts scheduled yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`upcoming-item-${item.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="font-bold text-[#1a2744] text-sm truncate">{item.title}</p>
                    <Badge variant="outline" className={`text-[10px] font-bold ${SCHED_STATUS_STYLES[item.status] || 'bg-slate-100 text-slate-700 border-slate-300'}`}>{item.status === 'approved' ? 'Approved' : 'Scheduled'}</Badge>
                    <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{SCHED_PLATFORMS[item.platform] || item.platform}</Badge>
                    {item.phase && <Badge variant="outline" className="text-[10px] border-[#C9A84C] text-[#7A5C12] bg-[#C9A84C]/10 font-bold">{item.phase}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {item.scheduled_date ? `${item.scheduled_date}${item.scheduled_time ? ` at ${item.scheduled_time}` : ''}` : 'No date set'}
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => copyItem(item)} className="h-8 text-xs font-bold" data-testid={`upcoming-copy-${item.id}`}><Copy className="h-3 w-3 mr-1" /> Copy</Button>
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`upcoming-edit-${item.id}`}>
                    <Link to={`/admin/scheduler?edit=${item.id}`}><Pencil className="h-3 w-3" /></Link>
                  </Button>
                  <Button size="sm" onClick={() => markPosted(item)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid={`upcoming-posted-${item.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Posted</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-3">Planning reminders only — nothing is ever posted automatically.</p>
      </CardContent>
    </Card>
  );
};

/* Phase 90: Needs Attention login/dashboard alert (admin-only) */
const ATTENTION_BUTTONS = [
  { label: 'Review All', to: '/admin/needs-attention', primary: true },
  { label: 'Issue Reports', to: '/admin/issues' },
  { label: 'PITA Requests', to: '/admin/pita-requests' },
  { label: 'Supporters', to: '/admin/network' },
  { label: 'Volunteers', to: '/admin/volunteers' },
  { label: 'Contact Messages', to: '/admin/messages' },
  { label: 'Story Media', to: '/admin/story-media' },
];

const AttentionAlert = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    const refresh = () => api.get('/admin/attention/summary').then((r) => setSummary(r.data)).catch(() => {});
    refresh();
    window.addEventListener('attention-changed', refresh);
    return () => window.removeEventListener('attention-changed', refresh);
  }, []);

  if (!summary || !summary.show_dashboard_alert) return null;

  if (!summary.total) {
    return (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5" data-testid="attention-alert-clear">
        <CheckCircle2 className="h-5 w-5 text-[#067647] shrink-0" />
        <p className="text-sm font-semibold text-emerald-900">No new items need attention right now.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#FBF7EE] border border-[#e5d5a5] rounded-xl p-4 mb-5" data-testid="attention-alert">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-[#C9A84C] text-[#1a2744] flex items-center justify-center shrink-0"><Bell className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-serif font-black text-[#1a2744]" data-testid="attention-alert-message">
            Steve, you have {summary.total} new item{summary.total === 1 ? '' : 's'} that need attention.
            {summary.urgent_count > 0 && <span className="text-red-700"> {summary.urgent_count} marked urgent.</span>}
          </p>
          <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="attention-alert-counts">
            {summary.counts.map((c) => (
              <li key={c.type} className="text-sm text-slate-700">
                <Link to={c.link} className="hover:underline font-semibold" data-testid={`attention-count-${c.type}`}>
                  {c.count} {c.label.toLowerCase()}
                </Link>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {ATTENTION_BUTTONS.map((b) => (
              <Button key={b.to + b.label} asChild size="sm"
                className={`h-9 text-xs font-bold btn-press ${b.primary ? 'bg-[#1a2744] hover:bg-[#243456] text-white' : 'bg-white border border-slate-300 text-[#1a2744] hover:bg-slate-50'}`}
                data-testid={`attention-btn-${b.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <Link to={b.to}>{b.label}</Link>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get('/admin/dashboard').then((r) => setData(r.data)).catch(() => {});
    api.get('/admin/notifications?unread_only=true').then((r) => setNotes(r.data.notifications || [])).catch(() => {});
  }, []);

  if (!data) return <Loading />;
  const c = data.cards;
  const ch = data.charts;

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">Dashboard</h1>
      <p className="text-sm text-slate-500 mb-4">Campaign platform overview · updated {formatDate(data.generated_at)}</p>

      {/* Needs Attention inbox alert (Phase 90) */}
      <AttentionAlert />

      {/* Private launch countdown banner (Phase 62) */}
      <LaunchCountdownBanner />

      {/* Critical alerts at top */}
      {(notes.length > 0 || c.issues_older_30 > 0 || c.contacts_unread > 0) && (
        <div className="space-y-2 mb-5" data-testid="dashboard-critical-alerts">
          {c.issues_older_30 > 0 && (
            <Link to="/admin/issues" className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3" data-testid="dashboard-alert-overdue">
              <AlertTriangle className="h-5 w-5 text-red-700 shrink-0" />
              <p className="text-sm font-semibold text-red-900">{c.issues_older_30} issue(s) open longer than 30 days need attention.</p>
            </Link>
          )}
          {notes.slice(0, 3).map((n) => (
            <Link key={n.id} to={n.link || '/admin'} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="dashboard-notification">
              <Bell className="h-5 w-5 text-amber-700 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900 truncate">{n.title}</p>
                <p className="text-xs text-amber-800 truncate">{n.message}</p>
              </div>
            </Link>
          ))}
          {notes.length > 0 && (
            <button onClick={async () => { await api.put('/admin/notifications/all/read'); setNotes([]); }} className="text-xs font-bold text-slate-500 underline underline-offset-4" data-testid="dashboard-clear-notifications">Mark all notifications read</button>
          )}
        </div>
      )}

      {/* Quick actions — big thumb-friendly buttons */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-9 gap-2 mb-6" data-testid="dashboard-quick-actions">
        {QUICK_ACTIONS.map(({ label, to, icon: Icon }) => (
          <Link key={label} to={to} data-testid={`quick-action-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div className="bg-[#1a2744] hover:bg-[#243456] text-white rounded-xl p-3 flex flex-col items-center gap-1.5 text-center btn-press min-h-[72px] justify-center">
              <Icon className="h-5 w-5 text-[#C9A84C]" />
              <span className="text-[10px] font-bold leading-tight">{label}</span>
            </div>
          </Link>
        ))}
      </div>

      {/* Network join request alerts */}
      <NetworkJoinAlert />

      {/* Design approval reminder (Phase 50) */}
      <DesignReminderCard />

      {/* Weekly digest auto-draft notice */}
      <WeeklyDigestNotice />

      {/* Monthly report reminder */}
      <MonthlyReportReminder />

      {/* Upcoming launch posts — planning reminders from the Post Scheduler */}
      <UpcomingLaunchPosts />

      {/* Drop day reminders (Phase 55) */}
      <DropDayRemindersCard />

      {/* Weekly Ops Brief Monday habit reminder (Phase 57) */}
      <WeeklyBriefReminderCard />

      {/* Quick setup reminders (Phase 58) */}
      <SetupRemindersCard />

      {/* Poster follow-up reminders (Phase 53) */}
      <PosterFollowUpsCard />

      {/* Reprint suggestion (Phase 54) */}
      <ReprintAlertCard />

      {/* Field ops weekly summary (Phase 55) */}
      <FieldOpsSummaryCard />

      {/* Materials running low (Phase 56) */}
      <MaterialsLowCard />

      {/* Top performing QR codes (Phase 52) */}
      <TopQrCodesCard />

      {/* Stat cards — stack 1 col mobile, 2 tablet, 4 desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-stat-cards">
        <Stat icon={FileWarning} label="Total issues" value={c.issues_total} to="/admin/issues" />
        <Stat icon={CircleDot} label="New issues" value={c.issues_new} to="/admin/issues" accent={c.issues_new > 0 ? 'bg-amber-50 text-amber-700' : undefined} />
        <Stat icon={Clock} label="Open issues" value={c.issues_open} to="/admin/issues" />
        <Stat icon={CheckCircle2} label="Resolved issues" value={c.issues_resolved} to="/admin/issues" accent="bg-emerald-50 text-emerald-700" />
        <Stat icon={AlertTriangle} label="Open > 14 days" value={c.issues_older_14} to="/admin/issues" accent={c.issues_older_14 > 0 ? 'bg-amber-50 text-amber-700' : undefined} />
        <Stat icon={AlertTriangle} label="Open > 30 days" value={c.issues_older_30} to="/admin/issues" accent={c.issues_older_30 > 0 ? 'bg-red-50 text-red-700' : undefined} />
        <Stat icon={BarChart3} label="Top issue category" value={c.top_category} to="/admin/issues" />
        <Stat icon={Clock} label="Average days open" value={c.avg_days_open} to="/admin/issues" />
        <Stat icon={HandHeart} label="Volunteers" value={c.volunteers} to="/admin/volunteers" />
        <Stat icon={Users} label="Newsletter subscribers" value={c.subscribers} to="/admin/newsletter" />
        <Stat icon={Inbox} label="Unread messages" value={c.contacts_unread} to="/admin/messages" accent={c.contacts_unread > 0 ? 'bg-amber-50 text-amber-700' : undefined} />
        <Stat icon={Newspaper} label="Published articles" value={c.news_published} to="/admin/newsroom" />
        <Stat icon={CalendarDays} label="Upcoming Council meetings" value={c.meetings_upcoming} to="/admin/meetings" />
        <Stat icon={Bell} label="Active alerts" value={c.alerts_active} to="/admin/alerts" />
        <Stat icon={Sparkles} label="Sample content needing review" value={c.sample_review} to="/admin/launch" accent={c.sample_review > 0 ? 'bg-amber-50 text-amber-700' : undefined} />
        <Stat icon={ImageOff} label="Placeholder media" value={c.placeholder_media} to="/admin/media-review" accent={c.placeholder_media > 0 ? 'bg-amber-50 text-amber-700' : undefined} />
        <Stat icon={Mail} label="Email provider" value={c.email_status === 'connected' ? 'Connected' : 'Mocked'} to="/admin/newsletter" accent={c.email_status === 'connected' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'} testId="admin-stat-email-provider" />
        <Stat icon={ShieldCheck} label="Privacy audit" value="Run check" to="/admin/privacy" testId="admin-stat-privacy" />
        <Stat icon={Activity} label="Post-Launch Pulse" value="Open" to="/admin/post-launch-pulse" testId="admin-stat-pulse" />
        <Stat icon={MessageCircle} label="Chats needing attention" value={c.chat_needs_attention} to="/admin/chat" />
        <Stat icon={Clock} label="Pending follow-ups" value={c.pending_followups} to="/admin/automation" />
      </div>

      {/* Charts — 1 col mobile, 2 col desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6" data-testid="dashboard-charts">
        <ChartCard title="Issues by category" testId="dashboard-chart-category">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ch.by_category} layout="vertical" margin={{ left: 8, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#1a2744" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Issues by status" testId="dashboard-chart-status">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ch.by_status} layout="vertical" margin={{ left: 8, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 9 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#C9A84C" radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Issues by community" testId="dashboard-chart-community">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ch.by_community}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Bar dataKey="value" fill="#3d6a8a" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Issues by month" testId="dashboard-chart-month">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ch.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#1a2744" strokeWidth={2.5} dot={{ fill: '#C9A84C', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Newsletter subscriber growth" testId="dashboard-chart-growth">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ch.newsletter_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#2d7a55" strokeWidth={2.5} dot={{ fill: '#2d7a55', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <Card data-testid="dashboard-analytics-note">
          <CardContent className="p-4 h-full flex flex-col justify-center">
            <p className="font-serif font-bold text-[#1a2744] text-sm mb-1">Website traffic</p>
            <p className="text-sm text-slate-500">Analytics are not connected yet. Traffic charts will appear here once an analytics provider is added after launch.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
